import { Injectable, } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from "@angular/router";
import * as Sentry from "@sentry/angular"
import { Observable } from 'rxjs';
import { AppService } from './app.service';
import { AuthService } from "./auth.service";
import { LangsRepository } from './langs.repository';


@Injectable()
export class AuthGuard {
    constructor(
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly appService: AppService,
        private langsRepository: LangsRepository,
    ) { }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        return new Promise((resolve, reject) => {
            this.authService.getMe().toPromise().then(response => {
                if (!response.body || response.body.responseCode !== 200 || !response.body.data) {
                    this.authService.logout();
                    this.authService.rememberOidcRetryRoute(state.url);
                    this.router.navigateByUrl("/auth/login");
                    this.appService.showError("Authentication error", response.body ? response.body.error : "Unauthorized", false, response.status);
                    resolve(false);
                    return;
                }

                const serverUser = response.body.data;
                this.authService.user = serverUser;
                // Always persist server user (e.g. after company switch reload) so companyId and other fields stay in sync
                const authToken = this.authService.getToken() || "";
                const authJwt = this.authService.getJwt() || "";
                this.authService.saveAuthData({ token: authToken, jwt: authJwt, user: serverUser });

                if (this.authService!.user!.id) {
                    Sentry.configureScope(scope => {
                      scope.setUser({
                          id: this.authService.user.id.toString(),
                          email: this.authService.user.email,
                          username: this.authService.user.firstName + " " + this.authService.user.lastName,
                      });
                    });
                }

                // The user is authenticated, but their session may be scoped to a different company
                // than the one the target deep link belongs to (e.g. a multi-company agency account).
                // Re-authenticate against the requested company instead of silently loading empty data.
                const requestedCompanyId = this.extractCompanyIdFromUrl(state.url);
                if (requestedCompanyId && serverUser.companyId && requestedCompanyId !== serverUser.companyId) {
                    this.authService.rememberOidcRetryRoute(state.url);
                    const callbackUrl = this.authService.getOidcCallbackUrl();
                    this.authService.startOidcLogin(callbackUrl, requestedCompanyId);
                    resolve(false);
                    return;
                }

                this.authService.clearPersistedOidcRetryRoute();
                resolve(true);
            }).catch(_err => {
                this.authService.logout();
                this.authService.prevUrl = state.url;

                const isCallbackRoute = state.url.indexOf('/auth/callback') >= 0;
                if (isCallbackRoute) {
                    this.router.navigateByUrl("/auth/login?error=auth_failed");
                    resolve(false);
                    return;
                }

                const retryValue = this.authService.getPersistedOidcRetryRoute();
                if (retryValue === state.url) {
                    this.router.navigateByUrl("/auth/login?error=auth_failed");
                    resolve(false);
                    return;
                }

                this.authService.rememberOidcRetryRoute(state.url);
                // OIDC-first login: don't show the legacy login page.
                const callbackUrl = this.authService.getOidcCallbackUrl();
                const companyId = this.extractCompanyIdFromUrl(state.url);
                this.authService.startOidcLogin(callbackUrl, companyId);

                resolve(false);
            });
        });
    }

    private extractCompanyIdFromUrl(url: string): number | undefined {
        const queryIndex = url.indexOf('?');
        const queryString = queryIndex >= 0 ? url.substring(queryIndex + 1) : '';
        const companyIdParam = new URLSearchParams(queryString).get('companyId');
        return companyIdParam ? parseInt(companyIdParam, 10) : undefined;
    }
}
